import { isEmpty, trim } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { LocationGroupScheduleComponentContext } from 'src/components/context';
import { DEFAULT_DURATION } from 'src/constants';
import { lang } from 'src/utils';

import { IPositionGridLines } from '../../../ScheduleComponentPage.interface';
import { useComponentListModal } from './useComponentListModal';

export const useScheduleComponentPageSidebar = () => {
  const {
    scheduleName,
    daypartName,
    schedule,
    layout,
    isCodConfirmationOpen,
    screenIndex,
    layerIndex,
    sectionIndex,
    screenSections,
    showSectionDetailDrawer,
    previewingPositionIndex,
    positionsGridLines,
    setScreenIndex,
    setShowLayoutScreenBuilder,
    setLayerIndex,
    onDeleteScreenSection,
    setSectionIndex,
    onUpdateScreensGridSectionsWhenSectionChanges,
    setShowSectionDetailDrawer,
    hadScheduledAllSections,
    hasAnyScheduledScreenForPosition,
    hasComponentsWithoutStartDate,
    hasInvalidBackups,
    setPreviewingPositionIndex,
    onUpdateScreensGridSectionsWhenAddingNewPosition,
    onUpdatePositionsGridLines,
    setScheduleName,
  } = useContext(LocationGroupScheduleComponentContext);

  const layoutTypeId = layout?.layout_type_id ?? -1;

  const selectedSection = screenSections[sectionIndex] ?? {};

  const [showScreensDrawer, setShowScreensDrawer] = useState(false);
  const [showScreenSectionsDrawer, setShowScreenSectionsDrawer] =
    useState(false);

  useEffect(() => {
    if (!positionsGridLines?.length) return;

    if (!isEmpty(schedule?.scheduleItems)) {
      const sortedByPosition = [...(schedule?.scheduleItems ?? [])].sort(
        (a, b) => a.position - b.position,
      );
      const firstItem = sortedByPosition[0];
      if (firstItem?.position !== undefined) {
        setPreviewingPositionIndex(firstItem.position);
      }
    }
  }, [
    sectionIndex,
    screenSections,
    schedule,
    positionsGridLines,
    setPreviewingPositionIndex,
  ]);
  const {
    showComponentListModal,
    filterParameters,
    modalTitle,
    removingInvalidBackupsConfirmationModal,
    onCloseComponentModal,
    onSelectedComponent,
    onOpenComponentModal,
    onReScheduleComponent,
    onAddAdditionalComponent,
    handleCloseInvalidBackupsConfirmationModal,
  } = useComponentListModal(
    layoutTypeId,
    screenSections[sectionIndex] ?? {},
    onUpdateScreensGridSectionsWhenSectionChanges,
    () => setShowSectionDetailDrawer(true),
  );

  const handleScheduleComponent = (index: number) => {
    setShowScreensDrawer(true);
    setLayerIndex(index);
  };

  const onCloseScreensDrawer = () => {
    setShowScreensDrawer(false);
    setLayerIndex(-1);
    setPreviewingPositionIndex(-1);
  };

  const onScheduleScreenComponent = useCallback(
    (screenIndex: number) => {
      setScreenIndex(screenIndex);
      setShowScreenSectionsDrawer(true);
      setShowLayoutScreenBuilder(true);
    },
    [setScreenIndex, setShowLayoutScreenBuilder],
  );

  const onCloseScreenSectionsDrawer = () => {
    setShowScreenSectionsDrawer(false);
    setShowLayoutScreenBuilder(false);
  };

  const onScheduleSectionComponent = (sectionIndex: number) => {
    setSectionIndex(sectionIndex);

    if (!screenSections[sectionIndex].components?.length) {
      onOpenComponentModal();
    } else {
      setShowSectionDetailDrawer(true);
    }
  };

  const addNewPosition = () => {
    const newPosition: IPositionGridLines = {
      position: positionsGridLines.length + 1,
      duration: DEFAULT_DURATION,
      gridLines: layout?.config?.gridLines ?? [],
    };

    onUpdateScreensGridSectionsWhenAddingNewPosition(newPosition);
    onUpdatePositionsGridLines(newPosition);
  };

  const onCloseSectionDetailDrawer = useCallback(() => {
    setShowSectionDetailDrawer(false);
  }, [setShowSectionDetailDrawer]);

  const onPreviewContent = (positionIndex: number, isPreviewing: boolean) => {
    setPreviewingPositionIndex(!isPreviewing ? -1 : positionIndex);
  };

  const onUpdatePositionDuration = (duration: number, position: number) => {
    onUpdatePositionsGridLines({
      position: position,
      duration: duration * 1000,
    });
  };

  const handleScheduleNameChange = (name: unknown) => {
    setScheduleName(name as string);
  };

  const validateScheduleName = (value: string): string | false => {
    const trimmed = trim(value);
    if (isEmpty(trimmed))
      return lang.scheduleComponent.scheduleNameFieldValidationMessage;
    if (trimmed.length > 255) return lang.scheduleComponent.scheduleNameError;

    return false;
  };

  return {
    isEditingSchedule: !isEmpty(schedule),
    scheduleName,
    daypartName,
    showScreensDrawer,
    layerIndex,
    screenIndex,
    showScreenSectionsDrawer,
    isCodConfirmationOpen,
    screens: layout?.screens ?? [],
    showSectionDetailDrawer,
    screenSections,
    positionsGridLines,
    previewingPositionIndex,
    showComponentListModal,
    filterParameters,
    selectedSection,
    modalTitle,
    removingInvalidBackupsConfirmationModal,
    onCloseScreensDrawer,
    onScheduleScreenComponent,
    onCloseScreenSectionsDrawer,
    onDeleteScreenSection,
    onScheduleSectionComponent,
    onCloseSectionDetailDrawer,
    onUpdateScreensGridSectionsWhenSectionChanges,
    hadScheduledAllSections,
    hasComponentsWithoutStartDate,
    hasInvalidBackups,
    onPreviewContent,
    onUpdatePositionDuration,
    onCloseComponentModal,
    onSelectedComponent,
    onReScheduleComponent,
    onAddAdditionalComponent,
    addNewPosition,
    handleScheduleComponent,
    handleScheduleNameChange,
    validateScheduleName,
    handleCloseInvalidBackupsConfirmationModal,
    hasAnyScheduledScreenForPosition,
  };
};
